import { Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';

import { asUnresolvedReason, asVerdict, ReconciledSegment } from './payload';
import { VerdictTag } from './VerdictTag';

type Props = {
  segments: ReconciledSegment[];
};

const em = (value: string | null | undefined) =>
  value ?? (
    <Text as="span" color="gray.400">
      —
    </Text>
  );

/** "2026-03-14" + "02:10" → "2026-03-14 02:10", each part optional. */
const when = (date: string | null | undefined, time: string | null | undefined) =>
  [date, time].filter(Boolean).join(' ') || null;

/**
 * Arrival, shown the same way as departure: the DATE the api resolved plus the time. The reviewer is approving what an
 * apply will write, and for an overnight flight that date is a day past the departure — so printing "21:45+1" and
 * leaving them to do the arithmetic is how a wrong day gets approved.
 */
const arrival = (segment: ReconciledSegment) => {
  if (segment.arrivalDate) return when(segment.arrivalDate, segment.arrivalTime);
  if (!segment.arrivalTime) return null;
  const offset = segment.extracted?.arrival_day_offset;
  return offset ? `${segment.arrivalTime}+${offset}` : segment.arrivalTime;
};

/**
 * The flights the carrier confirmed, each labelled against what we quoted. Times are shown as the carrier printed them
 * — local to their own airport — because that is what a reviewer is checking against the email beside it. Converting
 * them here would mean the table and the source email disagree.
 */
export const SegmentsTable = ({ segments }: Props) => (
  <Table size="sm" variant="simple">
    <Thead>
      <Tr>
        <Th>Flight</Th>
        <Th>Route</Th>
        <Th>Departs (local)</Th>
        <Th>Arrives (local)</Th>
        <Th>Against quote</Th>
      </Tr>
    </Thead>
    <Tbody>
      {segments.map((segment, index) => (
        <Tr key={`${segment.flightKey ?? 'unresolved'}-${index}`}>
          <Td fontWeight="medium">{em(segment.flightNumber ?? segment.extracted?.flight_number)}</Td>
          <Td>
            {em(
              segment.extracted?.origin && segment.extracted?.destination
                ? `${segment.extracted.origin} → ${segment.extracted.destination}`
                : null,
            )}
          </Td>
          <Td>{em(when(segment.departureDate, segment.departureTime))}</Td>
          <Td>{em(arrival(segment))}</Td>
          <Td>
            <VerdictTag verdict={asVerdict(segment.verdict)} unresolved={asUnresolvedReason(segment.unresolved)} />
          </Td>
        </Tr>
      ))}
    </Tbody>
  </Table>
);
